import type { Option } from "@tento-lona/sheets";
import { CellSlotDecorationRenderer } from "./decoration-renderer-cell-slot.ts";
import { FixedFrameDecorationRenderer } from "./decoration-renderer-fixed-frame.ts";
import type {
  DecorationPosition,
  DecorationRenderer,
  DecorationRendererContext,
  DecorationRendererFactory,
} from "./decoration-renderer.ts";

/**
 * Owns the decorations map and the active renderer. Prevents the
 * shared-mutable-alias bug: renderers may freely call
 * `this.decorations.clear()` in their `clear()` because this class
 * holds the authoritative reference and restores/replaces it as needed.
 */
export class SheetDecorations {
  #decorations = new Map<DecorationPosition, HTMLElement>();
  #renderer: Option<DecorationRenderer> = null;
  #fixedFrameEnabled = false;
  #rendererFactory: DecorationRendererFactory = (context) =>
    new CellSlotDecorationRenderer(context);

  readonly #getContext: () => DecorationRendererContext;
  constructor(
    getContext: () => DecorationRendererContext,
  ) {
    this.#getContext = getContext;
  }

  #getRenderer(): DecorationRenderer {
    if (!this.#renderer) {
      this.#renderer = this.#rendererFactory(this.#getContext());
      this.#renderer.set(this.#decorations);
    }
    return this.#renderer;
  }

  setFixedFrameEnabled(enabled: boolean): void {
    if (this.#fixedFrameEnabled === enabled) return;
    this.#fixedFrameEnabled = enabled;
    this.#rendererFactory = enabled
      ? (context) => new FixedFrameDecorationRenderer(context)
      : (context) => new CellSlotDecorationRenderer(context);
    // renderer.clear() may mutate its alias of the map — save and
    // restore so the new renderer receives the full decoration set.
    const saved = this.#decorations;
    this.#renderer?.clear();
    this.#decorations = saved;
    this.#renderer = null;
    this.rebind();
  }

  set(next: Map<DecorationPosition, HTMLElement>): void {
    this.#decorations = next;
    this.#getRenderer().set(next);
  }

  rebind(): void {
    this.#getRenderer().rebind();
  }

  clear(): void {
    this.#renderer?.clear();
    this.#decorations = new Map();
  }
}
